import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuthUser } from '../../hooks/useAuthUser';
import spinner from '../../assets/Spinner.svg';
import style from './GeneratedPlanPage.module.css';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import SecondaryButton from '../../components/SecondaryButton/SecondaryButton';
import { generateWorkoutRoutines } from '../../utils/generateRoutines';
import { toast } from 'react-toastify';
import {
	MdCalendarMonth,
	MdExpandMore,
	MdExpandLess,
	MdFitnessCenter,
	MdSelfImprovement,
} from 'react-icons/md';

const API_URL = process.env.REACT_APP_API_URL;

const PLAN_LOADING_MESSAGES = [
	'Our AI is crafting a personalized workout schedule tailored to your goals.',
	'Adding the perfect mix of push, pull, and leg days...',
	'Preparing your workout mat and setting the vibe.',
	'Hydrating the algorithm — gains require H₂O.',
	'Choosing exercises that’ll make you feel the burn (the good kind).',
	'Programming rest days so you recover like a pro.',
	'Sprinkling in some compound movements for maximum efficiency.',
	'Double-checking the equipment list so you’re never stuck.',
	'Balancing volume and intensity for your level.',
	'Pro tip: warm up for 5–10 minutes before each session.',
	'Pro tip: consistency beats perfection — show up and adjust as you go.',
	'Pro tip: sleep and nutrition are part of the program too.',
];

const GeneratedPlanPage = () => {
	const { user } = useAuthUser();
	const navigate = useNavigate();
	const location = useLocation();

	const [interests, setInterests] = useState(location.state?.planConfig || {});
	const [plan, setPlan] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [expandedDays, setExpandedDays] = useState({});
	const [activeWeek, setActiveWeek] = useState(1);
	const [savingProgress, setSavingProgress] = useState(0);
	const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

	const parseResponse = useCallback((text) => {
		if (!text || typeof text !== 'string') throw new Error('Empty response');
		const fenceMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
		const candidate = (fenceMatch ? fenceMatch[1] : text).trim();
		try { return JSON.parse(candidate); } catch (_) {
			const start = candidate.indexOf('{');
			const end = candidate.lastIndexOf('}');
			if (start !== -1 && end !== -1 && end > start) {
				return JSON.parse(candidate.slice(start, end + 1));
			}
			throw new Error('Failed to parse plan JSON');
		}
	}, []);

	const planDuration = useMemo(() => interests.planDuration || 7, [interests.planDuration]);

	useEffect(() => {
		if (!isLoading) return;
		const interval = setInterval(() => {
			setLoadingMessageIndex((i) => (i + 1) % PLAN_LOADING_MESSAGES.length);
		}, 3200);
		return () => clearInterval(interval);
	}, [isLoading]);

	const selectedEquipments = useMemo(() => {
		const raw = interests.workoutEquipments;
		if (Array.isArray(raw)) return raw;
		if (typeof raw === 'string' && raw) return raw.split(',').map(e => e.trim()).filter(Boolean);
		return [];
	}, [interests.workoutEquipments]);

	const isBodyweight = useMemo(
		() => String(interests.type || '').toLowerCase().includes('bodyweight'),
		[interests.type]
	);

	const equipmentsLabel = useMemo(
		() => (isBodyweight || !selectedEquipments.length) ? 'None' : selectedEquipments.join(', '),
		[isBodyweight, selectedEquipments]
	);

	const equipmentPolicy = useMemo(() => isBodyweight
		? `Equipment policy: Strictly NO EQUIPMENT. Only bodyweight exercises. Do NOT include any exercise requiring dumbbells, barbells, kettlebells, resistance bands, cables, machines, benches, boxes, TRX/suspension trainers, pull-up bars, or medicine balls.`
		: `Equipment policy: Use ONLY the following equipment if needed: [${equipmentsLabel}]. For exercises typically requiring unlisted equipment, choose an alternative using only allowed items or bodyweight.`,
		[isBodyweight, equipmentsLabel]
	);

	const prompt = useMemo(() => `You are an expert strength & conditioning coach designing a structured ${planDuration}-day workout program.

<user_profile>
  - Workout Type: ${interests.type || 'bodyweight'}
  - Interests: [${(interests.workoutInterests || []).join(', ')}]
  - Equipment: [${equipmentsLabel}]
  - Fitness Level: ${interests.level || 'Beginner'}
  - Workout Style: ${interests.style || 'Short and Intense'}
  - Session Duration: ${interests.duration || '30 mins'}
  - Age Range: ${interests.ageRange || 'Not specified'}
  - Gender: ${interests.gender || 'Not specified'}
  - Goal: ${interests.goal || 'General fitness'}
  - Additional Info: ${interests.additionalText || 'None'}
</user_profile>

${equipmentPolicy}

Programming principles:
1. Follow a push/pull/legs or upper/lower split pattern appropriate for the duration.
2. Place 1-2 rest days per 7-day block, strategically positioned after high-intensity days.
3. Progress across weeks: Week 1 builds foundation, later weeks increase volume or intensity.
4. Never train the same primary muscle group on consecutive days.
5. Each workout day must have exactly 5 specific, named exercises — NEVER generic categories.
6. Order exercises: compound movements first, then isolation, then core/finisher.
7. Adjust volume to fitness level (Beginner: 2-3 sets; Intermediate: 3-4 sets; Advanced: 4-5 sets).
8. Match session duration to user preference.
9. Zero exceptions to the equipment policy.
10. If the user's interests include "Pelvic Floor", incorporate pelvic floor exercises into appropriate workout days. Choose from: Kegels, Pelvic Tilts, Clamshells, Fire Hydrants, Marching Bridges, Diaphragmatic Breathing, Happy Baby Pose, or Sumo Squats. Use hold durations (e.g., "5-second holds") for reps when appropriate for isometric exercises.

Respond with ONLY a JSON object:

{
  "planName": "Short name (max 4 words)",
  "planDescription": "2-3 sentences on the plan's approach and expected results.",
  "days": [
    {
      "day": 1,
      "dayName": "Focus area label",
      "isRestDay": false,
      "focusArea": "Primary muscle groups",
      "estimatedTimeMinutes": 45,
      "workouts": [
        { "workoutType": "Specific Exercise Name", "workoutSets": "3 sets", "workoutReps": "12-15 reps" }
      ]
    },
    {
      "day": 3,
      "dayName": "Active Recovery",
      "isRestDay": true,
      "restTip": "Practical recovery advice"
    }
  ]
}

Generate exactly ${planDuration} day entries (day 1 through ${planDuration}). Rest days omit workouts/focusArea/estimatedTimeMinutes. Output ONLY the JSON — no markdown fences, no extra text.
`, [
		planDuration, interests.type, interests.workoutInterests, equipmentsLabel,
		interests.level, interests.style, interests.duration, interests.ageRange,
		interests.gender, interests.goal, interests.additionalText, equipmentPolicy,
	]);

	useEffect(() => {
		if (!location.state?.planConfig && user) {
			axios.get(`${API_URL}/interests/${user.id}`)
				.then(res => setInterests(res.data))
				.catch(err => console.error(err));
		}
	}, [user, location.state]);

	const planMaxTokens = useMemo(() => {
		if (planDuration >= 30) return 8192;
		if (planDuration >= 14) return 6144;
		return 4096;
	}, [planDuration]);

	useEffect(() => {
		if (Object.keys(interests).length === 0) return;
		setIsLoading(true);
		generateWorkoutRoutines(prompt, { maxTokens: planMaxTokens })
			.then((text) => {
				const parsed = parseResponse(text);
				setPlan(parsed);
				setIsLoading(false);
				if (parsed.days?.length) setExpandedDays({ 1: true });
			})
			.catch(err => {
				console.error('Plan generation failed:', err);
				setIsLoading(false);
			});
	}, [prompt, interests, parseResponse, planMaxTokens]);

	const totalWorkoutDays = useMemo(
		() => plan?.days?.filter(d => !d.isRestDay).length || 0,
		[plan]
	);

	const totalRestDays = useMemo(
		() => plan?.days?.filter(d => d.isRestDay).length || 0,
		[plan]
	);

	const weeks = useMemo(() => {
		if (!plan?.days) return [];
		const w = [];
		for (let i = 0; i < plan.days.length; i += 7) {
			w.push(plan.days.slice(i, i + 7));
		}
		return w;
	}, [plan]);

	const toggleDay = (dayNum) => {
		setExpandedDays(prev => ({ ...prev, [dayNum]: !prev[dayNum] }));
	};

	const acceptPlan = async () => {
		if (!plan || !plan.days) return;
		setIsSaving(true);
		setSavingProgress(0);

		try {
			const payload = {
				planName: plan.planName,
				planDescription: plan.planDescription || '',
				planDuration: planDuration,
				days: plan.days,
			};
			const res = await axios.post(`${API_URL}/users/${user.id}/workout-plans`, payload);
			const savedPlan = res.data;
			const planId = savedPlan._id;

			setSavingProgress(100);
			toast('Plan saved! You can manage it from your profile.');
			navigate(`/plan/${planId}`, { replace: true });
		} catch (error) {
			console.error('Failed to save plan:', error);
			toast('Failed to save plan. Please try again.');
		} finally {
			setIsSaving(false);
		}
	};

	if (isLoading) {
		return (
			<div className={style.LoadingState}>
				<div className={style.LoadingContent}>
					<img src={spinner} alt="" className={style.Spinner} />
					<h2 className={style.LoadingTitle}>Building your {planDuration}-day plan</h2>
					<p key={loadingMessageIndex} className={style.LoadingText}>
						{PLAN_LOADING_MESSAGES[loadingMessageIndex]}
					</p>
					<div className={style.LoadingDots}>
						<span className={style.Dot} />
						<span className={style.Dot} />
						<span className={style.Dot} />
					</div>
				</div>
			</div>
		);
	}

	if (!plan || !plan.days?.length) {
		return (
			<div className={style.ErrorState}>
				<h2>Something went wrong</h2>
				<p>We couldn't generate your plan. Please try again.</p>
				<SecondaryButton text="Try Again" click={() => window.location.reload()} />
			</div>
		);
	}

	return (
		<div className={style.GeneratedPlanPage}>
			<div className={style.PlanHeader}>
				<div className={style.PlanBadge}>
					<MdCalendarMonth />
					<span>{planDuration}-Day Plan</span>
				</div>
				<h1 className={style.PlanName}>{plan.planName}</h1>
				<p className={style.PlanDescription}>{plan.planDescription}</p>

				<div className={style.PlanStats}>
					<div className={style.Stat}>
						<MdFitnessCenter className={style.StatIcon} />
						<div className={style.StatContent}>
							<span className={style.StatValue}>{totalWorkoutDays}</span>
							<span className={style.StatLabel}>Workout Days</span>
						</div>
					</div>
					<div className={style.StatDivider} />
					<div className={style.Stat}>
						<MdSelfImprovement className={style.StatIcon} />
						<div className={style.StatContent}>
							<span className={style.StatValue}>{totalRestDays}</span>
							<span className={style.StatLabel}>Rest Days</span>
						</div>
					</div>
					<div className={style.StatDivider} />
					<div className={style.Stat}>
						<MdCalendarMonth className={style.StatIcon} />
						<div className={style.StatContent}>
							<span className={style.StatValue}>{planDuration}</span>
							<span className={style.StatLabel}>Total Days</span>
						</div>
					</div>
				</div>
			</div>

			{weeks.length > 1 && (
				<div className={style.WeekTabs}>
					{weeks.map((_, i) => (
						<button
							key={i}
							className={`${style.WeekTab} ${activeWeek === i + 1 ? style.WeekTabActive : ''}`}
							onClick={() => setActiveWeek(i + 1)}
						>
							Week {i + 1}
						</button>
					))}
				</div>
			)}

			<div className={style.DaysList}>
				{(weeks[activeWeek - 1] || []).map((day) => (
					<div
						key={day.day}
						className={`${style.DayCard} ${day.isRestDay ? style.RestDay : ''} ${expandedDays[day.day] ? style.DayExpanded : ''}`}
					>
						<div className={style.DayHeader}>
							<button className={style.DayHeaderToggle} onClick={() => !day.isRestDay && toggleDay(day.day)}>
								<div className={style.DayNumber}>
									<span className={style.DayNum}>{day.day}</span>
								</div>

								<div className={style.DayInfo}>
									<span className={style.DayName}>{day.dayName}</span>
									{day.isRestDay ? (
										<span className={style.RestLabel}>{day.restTip || 'Recovery & rest'}</span>
									) : (
										<span className={style.DayMeta}>
											{day.focusArea} · {day.estimatedTimeMinutes} min
										</span>
									)}
								</div>
							</button>
							<div className={style.DayActions}>
								{day.isRestDay ? (
									<div className={style.RestIcon}>
										<MdSelfImprovement />
									</div>
								) : (
									<button type='button' className={style.ExpandIcon} onClick={() => toggleDay(day.day)}>
										{expandedDays[day.day] ? <MdExpandLess /> : <MdExpandMore />}
									</button>
								)}
							</div>
						</div>

						{!day.isRestDay && expandedDays[day.day] && (
							<div className={style.DayWorkouts}>
								{(day.workouts || []).map((w, idx) => (
									<div key={idx} className={style.WorkoutRow}>
										<span className={style.WorkoutIndex}>{idx + 1}</span>
										<div className={style.WorkoutInfo}>
											<span className={style.WorkoutName}>{w.workoutType}</span>
											<span className={style.WorkoutDetail}>
												{w.workoutSets} · {w.workoutReps}
											</span>
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				))}
			</div>

			<div className={style.Actions}>
				{isSaving ? (
					<div className={style.SavingState}>
						<div className={style.SavingBar}>
							<div className={style.SavingFill} style={{ width: `${savingProgress}%` }} />
						</div>
						<span className={style.SavingText}>
							Saving routines... {savingProgress}%
						</span>
					</div>
				) : (
					<>
						<SecondaryButton text="Regenerate" click={() => window.location.reload()} />
						<PrimaryButton
							text="Accept Plan"
							click={acceptPlan}
						/>
					</>
				)}
			</div>
		</div>
	);
};

export default GeneratedPlanPage;
